import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { subscribe, getSnapshot as getAnimatedSnapshot } from '@/lib/positionAnimator';
import { isStationaryFix } from '@trackizy/formatters';

const STORAGE_KEY = 'trackizy_trails_enabled';
const SETTINGS_KEY = 'trackizy_trails_settings';

const DEFAULTS = { maxPoints: 30, maxMinutes: 5, minZoom: 10 };

// Module-level storage — survives component unmounts/remounts
const trailStore = {};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return { ...DEFAULTS };
}

/**
 * Hook that accumulates recent position history per device and builds
 * a GeoJSON FeatureCollection of LineStrings for rendering movement trails.
 */
export default function useTrails() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [settings, setSettings] = useState(loadSettings);
  const [trailGeoJSON, setTrailGeoJSON] = useState({ type: 'FeatureCollection', features: [] });

  const positions = useSelector((state) => state.session.positions);
  const devices = useSelector((state) => state.devices.items);
  // Animated displayed positions — when Live Map's inter-fix animation is
  // active, the marker sits 1 fix behind the latest raw fix. Without this,
  // the trail line would extend ahead of the arrow and the arrow would
  // appear to "catch up" to the trail. Substituting the trail's last coord
  // with the animated displayed coord keeps the line tail glued to the
  // arrow as it moves. Empty Map when animation is off → fall through to
  // the last raw coord.
  const animatedSnapshot = useSyncExternalStore(subscribe, getAnimatedSnapshot, getAnimatedSnapshot);

  const toggleTrails = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch { /* noop */ }
      if (!next) {
        Object.keys(trailStore).forEach((k) => delete trailStore[k]);
        setTrailGeoJSON({ type: 'FeatureCollection', features: [] });
      }
      return next;
    });
  }, []);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
      } catch { /* noop */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const now = Date.now();
    const trails = trailStore;
    const maxAge = settings.maxMinutes * 60 * 1000;
    const maxPts = settings.maxPoints;

    for (const [deviceIdStr, pos] of Object.entries(positions)) {
      const deviceId = Number(deviceIdStr);
      const device = devices[deviceId];
      if (!device || !pos) continue;
      if (!pos.latitude || !pos.longitude) continue;
      if (pos.latitude === 0 && pos.longitude === 0) continue;
      // Stationary fixes (parked + ignition off + GPS-jitter speed) don't
      // extend the trail. Without this, the line zigzags through 2–5 m
      // jitter clouds and the marker visibly walks around its parking
      // spot. The last appended point stays as the trail's terminus.
      if (isStationaryFix(pos)) continue;

      const point = {
        lng: pos.longitude,
        lat: pos.latitude,
        timestamp: pos.fixTime ? new Date(pos.fixTime).getTime() : now,
      };

      if (!trails[deviceId]) {
        trails[deviceId] = [];
      }

      const arr = trails[deviceId];

      if (arr.length > 0) {
        const last = arr[arr.length - 1];
        if (Math.abs(last.lng - point.lng) < 0.000001 && Math.abs(last.lat - point.lat) < 0.000001) continue;
      }

      arr.push(point);

      const cutoff = now - maxAge;
      while (arr.length > 0 && arr[0].timestamp < cutoff) {
        arr.shift();
      }
      while (arr.length > maxPts) {
        arr.shift();
      }
    }

    const features = [];
    for (const [deviceId, arr] of Object.entries(trails)) {
      if (arr.length < 2) continue;
      const coords = arr.map((p) => [p.lng, p.lat]);
      // Glue the trail's tail to the animated marker position when one
      // exists. The animated point lives between the last two raw fixes,
      // so swapping the last coord (target) for it shortens the line to
      // end exactly under the arrow.
      const animated = animatedSnapshot.get(Number(deviceId));
      if (
        animated &&
        Number.isFinite(animated.longitude) &&
        Number.isFinite(animated.latitude)
      ) {
        coords[coords.length - 1] = [animated.longitude, animated.latitude];
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {
          deviceId: Number(deviceId),
          pointCount: arr.length,
        },
      });
    }

    setTrailGeoJSON({ type: 'FeatureCollection', features });
  }, [enabled, positions, devices, settings, animatedSnapshot]);

  return { enabled, toggleTrails, trailGeoJSON, settings, updateSettings };
}
